import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { PreferencesProvider } from './context/PreferencesContext';
import { PdfConfigProvider } from './context/PdfConfigContext';
import { NotificationProvider } from './context/NotificationContext';
import { ThemeProvider } from './context/ThemeContext';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './App.css';

import AppLayout from './components/layout/AppLayout';
import ServerStatusMonitor from './components/ServerStatusMonitor';
import LandingLayout from './pages/Landing/LandingLayout';
import LandingPage from './pages/Landing/LandingPage';
import AboutPage from './pages/Landing/AboutPage';
import DerasarsPage from './pages/Landing/DerasarsPage';
import DerasarDetailPage from './pages/Landing/DerasarDetailPage';
import FeaturesPage from './pages/Landing/FeaturesPage';
import PrivacyPage from './pages/Landing/PrivacyPage';
import TermsPage from './pages/Landing/TermsPage';
import PricingPage from './pages/Landing/PricingPage';
import RefundPage from './pages/Landing/RefundPage';
import ContactPage from './pages/Landing/ContactPage';
import SupportPage from './pages/Landing/SupportPage';
import RequestDemoPage from './pages/Landing/RequestDemoPage';
import AppSupportPage from './pages/Support/AppSupportPage';
import SupportTicketsPage from './pages/SuperAdmin/SupportTicketsPage';
import DemoRequestsPage from './pages/SuperAdmin/DemoRequestsPage';
import TrialAccountsPage from './pages/SuperAdmin/TrialAccountsPage';
import LoginRegister from './pages/Auth/LoginRegister';
import AdminAuth from './pages/Auth/AdminAuth';
import SuperAdminLogin from './pages/Auth/SuperAdminLogin';
import VerifyEmail from './pages/Auth/VerifyEmail';
import PersonEmailVerification from './pages/Auth/PersonEmailVerification';
import ForgotPassword from './pages/Auth/ForgotPassword';
import ResetPassword from './pages/Auth/ResetPassword';
import ChangePasswordRequiredPage from './pages/Auth/ChangePasswordRequiredPage';
import AcceptInvitePage from './pages/Auth/AcceptInvitePage';
import EmailVerificationRequired from './pages/Auth/EmailVerificationRequired';
import OnboardingPage from './pages/Onboarding/OnboardingPage';
import SuperAdminPage from './pages/SuperAdmin/SuperAdminPage';
import NotificationUsage from './pages/SuperAdmin/NotificationUsage';
import NotificationChannels from './pages/SuperAdmin/NotificationChannels';
import Dashboard from './pages/Dashboard/Dashboard';
import BrowseDerasar from './pages/Derasar/BrowseDerasar';
import MyDerasar from './pages/Derasar/MyDerasar';
import OnlinePaymentBank from './pages/Derasar/OnlinePaymentBank';
import UsersPage from './pages/Derasar/UsersPage';
import CoAdminsPage from './pages/Derasar/CoAdminsPage';
import DonationCategories from './pages/DonationManagement/DonationCategories';
import UserFamilyPage from './pages/Family/UserFamilyPage';
import DerasarFamiliesPage from './pages/Family/DerasarFamiliesPage';
import PersonsPage from './pages/MyAccount/PersonsPage';
import AddressBookPage from './pages/Derasar/AddressBookPage';
import ProfilePage from './pages/Profile/ProfilePage';
import MyNotificationSettings from './pages/notificationSettings/MyNotificationSettings';
import PaymentPage from './pages/Payment/PaymentPage';
import RenewSubscriptionPage from './pages/Payment/RenewSubscriptionPage';
import SubscriptionSettings from './pages/SuperAdmin/SubscriptionSettings';
import OnlinePaymentSettings from './pages/SuperAdmin/OnlinePaymentSettings';
import PlatformAdminsPage from './pages/SuperAdmin/PlatformAdminsPage';
import FinancialYearsPage from './pages/Accounting/FinancialYearsPage';
import OpeningBalancePage from './pages/Accounting/OpeningBalance';
import ChartOfAccountsPage from './pages/Accounting/ChartOfAccountsPage';
import LedgerPage from './pages/Accounting/LedgerPage';
import OfferingsPage from './pages/Offerings/OfferingsPage';
import OfferingDetailPage from './pages/Offerings/OfferingDetailPage';
import ReceiptsPage from './pages/Receipts/ReceiptsPage';
import NewReceiptsPage from './pages/Receipts/NewReceiptsPage';
import EditReceiptsPage from './pages/Receipts/EditReceiptsPage';
import ReceiptDetailPage from './pages/Receipts/ReceiptDetailPage';
import PaymentsPage from './pages/Payments/PaymentsPage';
import NewPaymentsPage from './pages/Payments/NewPaymentsPage';
import EditPaymentsPage from './pages/Payments/EditPaymentsPage';
import JournalEntriesPage from './pages/JournalEntries/JournalEntriesPage';
import NewJournalEntryPage from './pages/JournalEntries/NewJournalEntryPage';
import UserPreferencesPage from './pages/Utils/UserPreferencesPage';
import PdfConfigPage from './pages/Utils/PdfConfigPage';
import ReportsPage from './pages/Reports/ReportsPage';
import MemberReportsPage from './pages/MemberReports/MemberReportsPage';
import PayOffersPage from './pages/PayOffers/PayOffersPage';
import EventsPage from './pages/Events/EventsPage';
import EventContributionsPage from './pages/Events/EventContributionsPage';
import BlindOfferPage from './pages/Events/BlindOfferPage';
import BlindOffersViewPage from './pages/Events/BlindOffersViewPage';
import NoticeBoardPage from './pages/Notices/NoticeBoardPage';
import CreateNoticePage from './pages/Notices/CreateNoticePage';
import ViewNoticePage from './pages/Notices/ViewNoticePage';
import ManageNoticesPage from './pages/Notices/ManageNoticesPage';
import EditNoticePage from './pages/Notices/EditNoticePage';
import PersonsMigrationPage from './pages/MyTools/PersonsMigrationPage';
import AnalyticsPage from './pages/analytics/AnalyticsPage';
import DerasarNotificationSettings from './pages/notificationSettings/DerasarNotificationSettings';

const PrivateRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="loading"><div className="spinner" /></div>;
  return user ? children : <Navigate to="/login" />;
};

const EmailVerifiedGuard = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="loading"><div className="spinner" /></div>;
  if (!user) return <Navigate to="/login" />;
  if (user.role !== 'super_admin' && !user.isEmailVerified) return <EmailVerificationRequired />;
  return children;
};

const PasswordChangeGuard = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="loading"><div className="spinner" /></div>;
  if (!user) return <Navigate to="/login" />;
  if (user.mustChangePassword) return <Navigate to="/change-password-required" />;
  return children;
};

const OnboardingGuard = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="loading"><div className="spinner" /></div>;
  if (!user) return <Navigate to="/login" />;
  if (!user.profileCompleted) return <Navigate to="/onboarding" />;
  return children;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="loading"><div className="spinner" /></div>;
  return user ? <Navigate to="/dashboard" /> : children;
};

const SuperAdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="loading"><div className="spinner" /></div>;
  if (!user) return <Navigate to="/superadmin/login" />;
  if (user.role !== 'super_admin' && user.role !== 'platform_admin') return <Navigate to="/dashboard" />;
  return children;
};

const MasterAdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="loading"><div className="spinner" /></div>;
  if (!user) return <Navigate to="/superadmin/login" />;
  if ((user.role !== 'super_admin' && user.role !== 'platform_admin') || !user.isSuperAdmin) return <Navigate to="/dashboard" />;
  return children;
};

const AdminRoute = ({ children }) => {
  const { user, loading, isDerasarAdmin } = useAuth();
  if (loading) return <div className="loading"><div className="spinner" /></div>;
  if (!user) return <Navigate to="/login" />;
  if (!isDerasarAdmin && user.role !== 'super_admin') return <Navigate to="/dashboard" />;
  return children;
};

const AnalyticsRoute = ({ children }) => {
  const { user, loading, isDerasarAdmin, can } = useAuth();
  if (loading) return <div className="loading"><div className="spinner" /></div>;
  if (!user) return <Navigate to="/login" />;
  // Platform/super admins always have access; derasar admins & co-admins need 'utils' view.
  if (user.role === 'super_admin' || user.role === 'platform_admin') return children;
  if (isDerasarAdmin && can('utils', 'view')) return children;
  return <Navigate to="/dashboard" />;
};

const RequirePermission = ({ module, level = 'view', children }) => {
  const { can, loading } = useAuth();
  if (loading) return <div className="loading"><div className="spinner" /></div>;
  return can(module, level) ? children : <Navigate to="/dashboard" replace />;
};

// Member-facing pages: regular members keep access; admins/co-admins need the module.
const MemberOrPermission = ({ module, level = 'view', children }) => {
  const { isDerasarAdmin, can, loading } = useAuth();
  if (loading) return <div className="loading"><div className="spinner" /></div>;
  return (!isDerasarAdmin || can(module, level)) ? children : <Navigate to="/dashboard" replace />;
};

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <PreferencesProvider>
        <PdfConfigProvider>
        <NotificationProvider>
          <BrowserRouter>
            <Routes>
            {/* Public info pages — LandingLayout wraps all with shared nav + footer */}
            <Route element={<LandingLayout />}>
              <Route path="/" element={<PublicRoute><LandingPage /></PublicRoute>} />
              <Route path="/about"               element={<AboutPage />} />
              <Route path="/derasars"            element={<DerasarsPage />} />
              <Route path="/derasars/:id/:slug?" element={<DerasarDetailPage />} />
              <Route path="/features"            element={<FeaturesPage />} />
              <Route path="/privacy"             element={<PrivacyPage />} />
              <Route path="/terms"               element={<TermsPage />} />
              <Route path="/pricing"            element={<PricingPage />} />
              <Route path="/refund"              element={<RefundPage />} />
              <Route path="/contact"             element={<ContactPage />} />
              <Route path="/support"             element={<SupportPage />} />
              <Route path="/request-demo"        element={<RequestDemoPage />} />
            </Route>

            {/* Auth routes */}
            <Route path="/login" element={<PublicRoute><LoginRegister /></PublicRoute>} />
            <Route path="/login/admin" element={<PublicRoute><AdminAuth /></PublicRoute>} />
            <Route path="/superadmin/login" element={<PublicRoute><SuperAdminLogin /></PublicRoute>} />
            <Route path="/payment" element={<PublicRoute><PaymentPage /></PublicRoute>} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password/:token" element={<ResetPassword />} />
            <Route path="/verify-email/:token" element={<VerifyEmail />} />
            <Route path="/verify-person-email/:token" element={<PersonEmailVerification />} />
            <Route path="/accept-invite/:token" element={<AcceptInvitePage />} />

            {/* Forced password change */}
            <Route path="/change-password-required" element={<PrivateRoute><ChangePasswordRequiredPage /></PrivateRoute>} />

            {/* Onboarding */}
            <Route path="/onboarding" element={<PrivateRoute><PasswordChangeGuard><EmailVerifiedGuard><OnboardingPage /></EmailVerifiedGuard></PasswordChangeGuard></PrivateRoute>} />

            {/* Protected routes */}
            <Route element={<PrivateRoute><PasswordChangeGuard><EmailVerifiedGuard><AppLayout /></EmailVerifiedGuard></PasswordChangeGuard></PrivateRoute>}>
              <Route path="/dashboard" element={<OnboardingGuard><Dashboard /></OnboardingGuard>} />

              {/* Super Admin */}
              <Route path="/super-admin" element={<SuperAdminRoute><SuperAdminPage /></SuperAdminRoute>} />
              <Route path="/super-admin/settings" element={<MasterAdminRoute><SubscriptionSettings /></MasterAdminRoute>} />
              <Route path="/super-admin/admins" element={<MasterAdminRoute><PlatformAdminsPage /></MasterAdminRoute>} />
              <Route path="/super-admin/support" element={<SuperAdminRoute><SupportTicketsPage /></SuperAdminRoute>} />
              <Route path="/super-admin/demo-requests" element={<SuperAdminRoute><DemoRequestsPage /></SuperAdminRoute>} />
              <Route path="/super-admin/trial-accounts" element={<SuperAdminRoute><TrialAccountsPage /></SuperAdminRoute>} />
              <Route path="/super-admin/notification-channels" element={<SuperAdminRoute><NotificationChannels /></SuperAdminRoute>} />
              <Route path="/super-admin/notification-usage" element={<SuperAdminRoute><NotificationUsage /></SuperAdminRoute>} />
              <Route path="/super-admin/online-payments" element={<SuperAdminRoute><OnlinePaymentSettings /></SuperAdminRoute>} />

              {/* Derasar */}
              <Route path="/derasar/browse" element={<BrowseDerasar />} />
              <Route path="/derasar/my" element={<MyDerasar />} />
              <Route path="/derasar/events" element={<MemberOrPermission module="eventsNotices"><EventsPage /></MemberOrPermission>} />
              <Route path="/derasar/events/:id/contributions" element={<MemberOrPermission module="eventsNotices"><EventContributionsPage /></MemberOrPermission>} />
              <Route path="/derasar/events/:eventId/blind-offer" element={<MemberOrPermission module="eventsNotices"><BlindOfferPage /></MemberOrPermission>} />
              <Route path="/derasar/events/:eventId/blind-offers" element={<MemberOrPermission module="eventsNotices"><BlindOffersViewPage /></MemberOrPermission>} />
              <Route path="/derasar/notices" element={<MemberOrPermission module="eventsNotices"><NoticeBoardPage /></MemberOrPermission>} />
              <Route path="/derasar/notices/create" element={<AdminRoute><RequirePermission module="eventsNotices" level="manage"><CreateNoticePage /></RequirePermission></AdminRoute>} />
              <Route path="/derasar/notices/manage" element={<AdminRoute><RequirePermission module="eventsNotices" level="manage"><ManageNoticesPage /></RequirePermission></AdminRoute>} />
              <Route path="/derasar/notices/:id" element={<MemberOrPermission module="eventsNotices"><ViewNoticePage /></MemberOrPermission>} />
              <Route path="/derasar/notices/:id/edit" element={<AdminRoute><RequirePermission module="eventsNotices" level="manage"><EditNoticePage /></RequirePermission></AdminRoute>} />

              {/* Profile */}
              <Route path="/profile" element={<ProfilePage />} />
              <Route path="/notification-preferences" element={<MyNotificationSettings />} />
              <Route path="/subscription/renew" element={<AdminRoute><RenewSubscriptionPage /></AdminRoute>} />
              <Route path="/family" element={<UserFamilyPage />} />
              <Route path="/derasar/families" element={<AdminRoute><RequirePermission module="members"><DerasarFamiliesPage /></RequirePermission></AdminRoute>} />
              <Route path="/my-persons" element={<PersonsPage />} />

              {/* Administration (admin only) */}
              <Route path="/management/categories" element={<AdminRoute><RequirePermission module="donationCategories"><DonationCategories /></RequirePermission></AdminRoute>} />
              <Route path="/management/co-admins" element={<AdminRoute><CoAdminsPage /></AdminRoute>} />
              <Route path="/users" element={<AdminRoute><RequirePermission module="members"><UsersPage /></RequirePermission></AdminRoute>} />
              <Route path="/address-book" element={<AddressBookPage />} />

              {/* Accounting — setup */}
              <Route path="/accounting/financial-years" element={<AdminRoute><RequirePermission module="accountingSetup"><FinancialYearsPage /></RequirePermission></AdminRoute>} />
              <Route path="/accounting/opening-balance/:yearId" element={<AdminRoute><RequirePermission module="accountingSetup"><OpeningBalancePage /></RequirePermission></AdminRoute>} />
              <Route path="/accounting/chart-of-accounts" element={<AdminRoute><RequirePermission module="accountingSetup"><ChartOfAccountsPage /></RequirePermission></AdminRoute>} />
              <Route path="/accounting/ledger" element={<AdminRoute><RequirePermission module="accountingSetup"><LedgerPage /></RequirePermission></AdminRoute>} />

              {/* Accounting — transactions */}
              <Route path="/accounting/offerings" element={<MemberOrPermission module="offerings"><OfferingsPage /></MemberOrPermission>} />
              <Route path="/accounting/offerings/:id" element={<MemberOrPermission module="offerings"><OfferingDetailPage /></MemberOrPermission>} />
              <Route path="/accounting/receipts" element={<MemberOrPermission module="receipts"><ReceiptsPage /></MemberOrPermission>} />
              <Route path="/accounting/receipts/new" element={<AdminRoute><RequirePermission module="receipts" level="manage"><NewReceiptsPage /></RequirePermission></AdminRoute>} />
              <Route path="/accounting/receipts/:id/edit" element={<AdminRoute><RequirePermission module="receipts" level="manage"><EditReceiptsPage /></RequirePermission></AdminRoute>} />
              <Route path="/accounting/receipts/:id" element={<MemberOrPermission module="receipts"><ReceiptDetailPage /></MemberOrPermission>} />
              <Route path="/accounting/payments" element={<AdminRoute><RequirePermission module="payments"><PaymentsPage /></RequirePermission></AdminRoute>} />
              <Route path="/accounting/payments/new" element={<AdminRoute><RequirePermission module="payments" level="manage"><NewPaymentsPage /></RequirePermission></AdminRoute>} />
              <Route path="/accounting/payments/:id/edit" element={<AdminRoute><RequirePermission module="payments" level="manage"><EditPaymentsPage /></RequirePermission></AdminRoute>} />
              <Route path="/accounting/journal-entries" element={<AdminRoute><RequirePermission module="journalEntries"><JournalEntriesPage /></RequirePermission></AdminRoute>} />
              <Route path="/accounting/journal-entries/new" element={<AdminRoute><RequirePermission module="journalEntries" level="manage"><NewJournalEntryPage /></RequirePermission></AdminRoute>} />
              <Route path="/utils/pdf-config"       element={<PrivateRoute><RequirePermission module="utils"><PdfConfigPage /></RequirePermission></PrivateRoute>} />
              <Route path="/management/notification-settings" element={<AdminRoute><RequirePermission module="utils" level="manage"><DerasarNotificationSettings /></RequirePermission></AdminRoute>} />
              <Route path="/management/online-payments" element={<AdminRoute><RequirePermission module="utils" level="manage"><OnlinePaymentBank /></RequirePermission></AdminRoute>} />
              <Route path="/preferences" element={<PrivateRoute><UserPreferencesPage /></PrivateRoute>} />
              <Route path="/my-tools/persons-migration" element={<PrivateRoute><PersonsMigrationPage /></PrivateRoute>} />

              {/* Accounting — reports */}
              <Route path="/accounting/reports" element={<AdminRoute><RequirePermission module="reports"><ReportsPage /></RequirePermission></AdminRoute>} />
              <Route path="/member-reports" element={<PrivateRoute><MemberReportsPage /></PrivateRoute>} />
              <Route path="/pay-offers" element={<PrivateRoute><PayOffersPage /></PrivateRoute>} />

              {/* Analytics */}
              <Route path="/analytics" element={<AnalyticsRoute><AnalyticsPage /></AnalyticsRoute>} />

              {/* Help & Support */}
              <Route path="/help" element={<AppSupportPage />} />
            </Route>

            <Route path="*" element={<Navigate to="/" />} />
            </Routes>
          </BrowserRouter>
        </NotificationProvider>
        </PdfConfigProvider>
        </PreferencesProvider>
        <ServerStatusMonitor />
        <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} />
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
